/**
 * Nueva orden de pedido — formulario de alta.
 * Formato, diseño y seguridad alineados con FacturaCredito.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  MenuItem,
  Alert,
  Chip,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  ShoppingCart as ShoppingCartIcon,
  Person as PersonIcon,
  LockOutlined as LockOutlinedIcon,
  Close as CloseIcon,
  InfoOutlined as InfoOutlinedIcon,
  ReceiptLong as ReceiptLongIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import {
  tienePermisosModulo,
  puedeEditarProgramaFacturacion,
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, BarraVolver, fpInputSx } from 'components/financepro';
import ConsultaProducto from 'productosnoserializados/ConsultaProducto';

export const PROGRAMA_ORDEN_PEDIDO = 'ordenesdepedido';

const TIPO_FACTURA_CREDITO = 'FACTURA CREDITO';
const TIPO_FACTURA_CONTADO = 'FACTURA CONTADO';

const cardShellSx = {
  borderRadius: 2.5,
  border: `1px solid ${FP.color.border}`,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  overflow: 'hidden',
};

const filterHeaderSx = {
  px: { xs: 2, md: 3 },
  py: 1.6,
  borderBottom: `1px solid ${FP.color.border}`,
  bgcolor: '#FAFBFE',
  display: 'flex',
  alignItems: 'center',
  gap: 1.2,
};

const cellHeadSx = {
  fontWeight: 700,
  fontSize: '0.75rem',
  color: FP.color.textSecondary,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  display: 'block',
  mb: 0.5,
};

const fmtMoney = (v) =>
  (parseFloat(v) || 0).toLocaleString('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/**
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {() => void} [onGuardado]
 * @param {number|string|null} [ordenId] — si viene, abre en modo edición
 */
const NuevaOrdenPedido = ({ open, onClose, onGuardado, ordenId = null }) => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { nombreNegocio, usuarioLog, claveLog, roleDetalles } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const permisoLeer = useMemo(
    () => tienePermisosModulo(roleDetalles, PROGRAMA_ORDEN_PEDIDO),
    [roleDetalles]
  );
  const permisoModificar = useMemo(
    () => puedeEditarProgramaFacturacion(roleDetalles, PROGRAMA_ORDEN_PEDIDO),
    [roleDetalles]
  );

  const esEdicion = Boolean(ordenId);
  const hoy = new Date().toISOString().slice(0, 10);

  const [clientes, setClientes] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [configCliente, setConfigCliente] = useState(null);
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [listasPrecios, setListasPrecios] = useState([]);
  const [listaPrecioCliente, setListaPrecioCliente] = useState(null);
  // 'detalle' | 'mayor': pasa a 'mayor' solo cuando el cliente tiene lista.
  const [tipoPrecio, setTipoPrecio] = useState('detalle');
  const [productos, setProductos] = useState([]);
  const [items, setItems] = useState([]);
  const [tipoFactura, setTipoFactura] = useState('credito');
  const [diasCredito, setDiasCredito] = useState('');
  const [formaPago, setFormaPago] = useState('');
  const [validezDias, setValidezDias] = useState('30');
  const [observaciones, setObservaciones] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [cargando, setCargando] = useState(false);

  const [dialogoProductoAbierto, setDialogoProductoAbierto] = useState(false);
  const [dialogoConsultaProducto, setDialogoConsultaProducto] = useState(false);
  const [busquedaProducto, setBusquedaProducto] = useState('');
  const [cantidadesPorProducto, setCantidadesPorProducto] = useState({});
  const prevListaPrecioIdRef = useRef(undefined);
  const skipLimpiarItemsRef = useRef(false);

  const subtotal = useMemo(
    () => items.reduce((s, it) => s + (parseFloat(it.subtotal) || 0), 0),
    [items]
  );

  const getCantidadProducto = (codigo) => cantidadesPorProducto[codigo] ?? 1;
  const setCantidadProducto = (codigo, valor) => {
    const num = Math.max(0.01, parseFloat(valor) || 1);
    setCantidadesPorProducto((prev) => ({ ...prev, [codigo]: num }));
  };

  const limpiarFormulario = useCallback(() => {
    setClienteSeleccionado(null);
    setConfigCliente(null);
    setBusquedaCliente('');
    setListaPrecioCliente(null);
    setItems([]);
    setTipoFactura('credito');
    setDiasCredito('');
    setFormaPago('');
    setValidezDias('30');
    setObservaciones('');
    setBusquedaProducto('');
    setCantidadesPorProducto({});
    prevListaPrecioIdRef.current = undefined;
    skipLimpiarItemsRef.current = false;
  }, []);

  const cargarClientes = useCallback(async () => {
    try {
      const response = await fetch(`${servidor}/api/clientesbackend_buscar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tipo: 'Cliente',
          descripcion: '',
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setClientes(data.resultados || data.clientes || []);
      }
    } catch {
      /* silencioso */
    }
  }, [servidor, selectedCompany, usuarioLog, claveLog]);

  const cargarListasPrecios = useCallback(async () => {
    try {
      const response = await fetch(`${servidor}/api/productosbackend_listarlistasprecios`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setListasPrecios(data.listas || []);
      }
    } catch {
      /* silencioso */
    }
  }, [servidor, selectedCompany, usuarioLog, claveLog]);

  const cargarConfigCliente = useCallback(
    async (clienteId) => {
      try {
        const response = await fetch(`${servidor}/api/clientesbackend_obtenerporid`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            id: clienteId,
          }),
        });
        if (response.ok) {
          const data = await response.json();
          const cfg = data.cliente || null;
          setConfigCliente(cfg);
          if (cfg?.diascredito != null && cfg.diascredito !== '') {
            setDiasCredito(String(cfg.diascredito));
          }
        } else {
          setConfigCliente(null);
        }
      } catch {
        setConfigCliente(null);
      }
    },
    [servidor, selectedCompany, usuarioLog, claveLog]
  );

  const localidadAlmacenPrincipal = useMemo(
    () => localStorage.getItem('sucursal') || '',
    []
  );

  const cargarProductos = useCallback(async () => {
    try {
      const response = await fetch(`${servidor}/api/facturabackend_obtenerproductos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          anomesfacturacion: 'productosnoserializados',
          lista_precio_id: listaPrecioCliente?.id ?? null,
          tipo_precio: tipoPrecio,
          localidad: localidadAlmacenPrincipal || '0',
        }),
      });
      if (response.ok) {
        const data = await response.json();
        let prods = data.productos || [];
        if (tipoPrecio === 'mayor' && listaPrecioCliente && listasPrecios.length > 0) {
          const lista = listasPrecios.find((l) => l.id === listaPrecioCliente.id);
          if (lista) {
            const pct = 1 - (parseFloat(lista.descuento_precio_final) || 0) / 100;
            // El backend ya devolvió el precio `mayor` del producto cuando lo
            // tiene (precio_origen 'mayor'); el % de la lista solo se aplica
            // a los que quedaron con precio de detalle.
            prods = prods.map((p) =>
              p.precio_origen === 'mayor'
                ? p
                : { ...p, precio: (parseFloat(p.precio) || 0) * pct }
            );
          }
        }
        setProductos(prods);
        return prods;
      }
    } catch {
      /* silencioso */
    }
    return null;
  }, [
    servidor,
    selectedCompany,
    usuarioLog,
    claveLog,
    listaPrecioCliente,
    listasPrecios,
    localidadAlmacenPrincipal,
    tipoPrecio,
  ]);

  // Reprecia las líneas de producto con la lista recién cargada (cambio manual
  // Detalle/Mayor sin perder lo ya digitado).
  const repreciarItems = (lista, setter, calcDescuento) => {
    setter((prev) =>
      prev.map((item) => {
        if (item.tipo !== 'producto') return item;
        const prod = lista.find((p) => p.codigo === item.codigo);
        if (!prod) return item;
        const precio = parseFloat(prod.precio) || 0;
        const cantidad = parseFloat(item.cantidad) || 0;
        const descuento = calcDescuento ? calcDescuento(prod, cantidad) : parseFloat(item.descuento) || 0;
        return { ...item, precio, descuento, subtotal: Math.max(0, cantidad * precio - descuento) };
      })
    );
  };

  useEffect(() => {
    if (!open) return undefined;
    limpiarFormulario();
    if (!permisoLeer) return undefined;
    let cancelado = false;
    (async () => {
      setCargando(true);
      await Promise.all([cargarClientes(), cargarListasPrecios(), cargarProductos()]);
      if (cancelado) return;
      if (ordenId) {
        try {
          const resp = await fetch(`${servidor}/api/ordenesdepedidos_obtener`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id_empresa: selectedCompany,
              usuario: usuarioLog || '',
              password: claveLog || '',
              orden_id: ordenId,
            }),
          });
          if (resp.ok) {
            const d = await resp.json();
            const orden = d.orden || {};
            const estado = String(orden.estado || '').trim().toUpperCase();
            if (estado === 'FACTURADA' || estado === 'CERRADA') {
              showError('La orden está cerrada o facturada y no se puede modificar');
              if (typeof onClose === 'function') onClose();
              return;
            }
            const tipo = String(orden.tipo_factura || '').toUpperCase();
            setTipoFactura(tipo.includes('CONTADO') ? 'contado' : 'credito');
            if (orden.dias_credito != null && orden.dias_credito !== '') setDiasCredito(String(orden.dias_credito));
            setFormaPago(orden.forma_pago || '');
            setValidezDias(orden.validez_dias != null ? String(orden.validez_dias) : '30');
            setObservaciones(orden.observaciones || '');
            const clienteId = String(orden.cliente_id || '').trim();
            const clienteObj = clienteId
              ? { id: clienteId, descrip: orden.cliente || clienteId, descripcion: orden.cliente || clienteId, direccion: orden.direccion || '', telefono: orden.telefono || '' }
              : null;
            setClienteSeleccionado(clienteObj);
            if (clienteObj) setBusquedaCliente(`${clienteObj.id} - ${clienteObj.descrip}`);
            const itemsMapped = (d.items || []).map((it, idx) => ({
              id: it.id || `${Date.now()}-${idx}`,
              codigo: it.codigo, descripcion: it.descripcion || '',
              cantidad: parseFloat(it.cantidad) || 1, precio: parseFloat(it.precio) || 0,
              descuento: parseFloat(it.descuento) || 0, impuesto: parseFloat(it.impuesto) || 0,
              subtotal: parseFloat(it.subtotal) || 0,
              tipo: it.tipo_item || 'producto', producto_codigo: it.producto_codigo || it.codigo,
            }));
            setItems(itemsMapped);
            skipLimpiarItemsRef.current = true;
          } else {
            const err = await resp.json().catch(() => ({}));
            showError(err.Error || 'No se pudo cargar la orden');
          }
        } catch { showError('Error de conexión al cargar la orden'); }
      }
      if (!cancelado) setCargando(false);
    })();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, permisoLeer, ordenId]);

  useEffect(() => {
    if (clienteSeleccionado?.id) {
      cargarConfigCliente(clienteSeleccionado.id);
    } else {
      setConfigCliente(null);
    }
  }, [clienteSeleccionado?.id, cargarConfigCliente]);

  useEffect(() => {
    if (!clienteSeleccionado) {
      setListaPrecioCliente(null);
      return;
    }
    if (listasPrecios.length === 0) return;
    const listaId = configCliente?.lista_precio_id ?? clienteSeleccionado?.lista_precio_id;
    if (listaId) {
      const lista = listasPrecios.find((l) => l.id === listaId);
      setListaPrecioCliente(lista || null);
    } else {
      setListaPrecioCliente(null);
    }
  }, [
    clienteSeleccionado,
    configCliente?.lista_precio_id,
    clienteSeleccionado?.lista_precio_id,
    listasPrecios,
  ]);

  // Con lista asignada al cliente se pasa a precio mayor automáticamente.
  useEffect(() => {
    setTipoPrecio(listaPrecioCliente ? 'mayor' : 'detalle');
  }, [listaPrecioCliente?.id]);

  const prevTipoPrecioRef = useRef('detalle');
  useEffect(() => {
    if (!open || !permisoLeer) return;
    const cambioLista = prevListaPrecioIdRef.current !== listaPrecioCliente?.id;
    const cambioTipo = prevTipoPrecioRef.current !== tipoPrecio;
    if (!cambioLista && !cambioTipo) return;
    prevListaPrecioIdRef.current = listaPrecioCliente?.id;
    prevTipoPrecioRef.current = tipoPrecio;
    if (!cambioLista) {
      // Solo cambió Detalle/Mayor a mano: recargar y repreciar lo digitado.
      cargarProductos().then((lista) => {
        if (lista) repreciarItems(lista, setItems, null);
      });
      return;
    }
    cargarProductos();
    if (skipLimpiarItemsRef.current) {
      skipLimpiarItemsRef.current = false;
    } else {
      setItems([]);
      setCantidadesPorProducto({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listaPrecioCliente?.id, tipoPrecio, open, permisoLeer]);

  const clientesFiltrados = useMemo(() => {
    if (!busquedaCliente.trim()) return clientes.slice(0, 100);
    const b = busquedaCliente.toLowerCase();
    return clientes
      .filter(
        (c) =>
          String(c.id || '').includes(b) ||
          String(c.descrip || c.descripcion || '').toLowerCase().includes(b)
      )
      .slice(0, 100);
  }, [clientes, busquedaCliente]);

  const productosFiltrados = useMemo(() => {
    if (!busquedaProducto.trim()) return productos.slice(0, 50);
    const b = busquedaProducto.toLowerCase();
    return productos
      .filter(
        (p) =>
          String(p.codigo || '').toLowerCase().includes(b) ||
          String(p.descripcion || '').toLowerCase().includes(b)
      )
      .slice(0, 50);
  }, [productos, busquedaProducto]);

  const agregarProducto = (producto, cantidadForzada) => {
    if (!permisoModificar) {
      showError('No tiene permiso para modificar la orden');
      return;
    }
    const cant = Math.max(0.01, parseFloat(cantidadForzada ?? getCantidadProducto(producto.codigo)) || 1);
    const precio = parseFloat(producto.precio) || 0;
    const nuevo = {
      id: Date.now() + Math.random(),
      codigo: producto.codigo,
      descripcion: producto.descripcion || '',
      cantidad: cant,
      precio,
      descuento: 0,
      impuesto: 0,
      subtotal: Math.max(0, cant * precio),
      tipo: 'producto',
      es_inventario: true,
      producto_codigo: producto.codigo,
      existencia: parseFloat(producto.existencia) || 0,
    };
    setItems((prev) => [...prev, nuevo]);
    setDialogoProductoAbierto(false);
    setBusquedaProducto('');
  };

  const quitarItem = (id) => {
    if (!permisoModificar) return;
    setItems((prev) => prev.filter((i) => i.id !== id));
  };

  const actualizarCantidad = (id, valor) => {
    if (!permisoModificar) return;
    const num = Math.max(0.01, parseFloat(valor) || 1);
    setItems((prev) =>
      prev.map((it) =>
        it.id === id
          ? {
              ...it,
              cantidad: num,
              subtotal: Math.max(
                0,
                num * (parseFloat(it.precio) || 0) - (parseFloat(it.descuento) || 0)
              ),
            }
          : it
      )
    );
  };

  const validarClienteYLimites = () => {
    if (!clienteSeleccionado) {
      showError('Seleccione un cliente');
      return false;
    }
    if (!configCliente) return true;
    const limCredito = parseFloat(configCliente.limcredito) || 0;
    const limCompra = parseFloat(configCliente.limcompra) || 0;
    if (tipoFactura === 'credito' && limCredito > 0 && subtotal > limCredito) {
      showError(
        `El total (${subtotal.toFixed(2)}) supera el límite de crédito del cliente (${limCredito.toFixed(2)})`
      );
      return false;
    }
    if (limCompra > 0 && subtotal > limCompra) {
      showError(
        `El total (${subtotal.toFixed(2)}) supera el límite de compra del cliente (${limCompra.toFixed(2)})`
      );
      return false;
    }
    return true;
  };

  const guardarOrden = async () => {
    if (!permisoModificar) {
      showError('No tiene permiso para crear órdenes de pedido');
      return;
    }
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    if (items.length === 0) {
      showError('Agregue al menos un ítem');
      return;
    }
    if (!validarClienteYLimites()) return;

    setGuardando(true);
    try {
      const payloadOrden = {
        ...(esEdicion ? { id: ordenId } : {}),
        cliente_id: String(clienteSeleccionado.id || ''),
        cliente:
          clienteSeleccionado.descrip ||
          clienteSeleccionado.descripcion ||
          String(clienteSeleccionado.id || ''),
        direccion:
          clienteSeleccionado.direccion ||
          configCliente?.direccion ||
          '',
        telefono:
          clienteSeleccionado.telefono ||
          clienteSeleccionado.tel ||
          configCliente?.telefono ||
          '',
        fecha: hoy,
        tipo_factura: tipoFactura === 'credito' ? TIPO_FACTURA_CREDITO : TIPO_FACTURA_CONTADO,
        dias_credito: tipoFactura === 'credito' ? diasCredito : null,
        forma_pago: formaPago,
        validez_dias: validezDias,
        subtotal,
        impuestos: 0,
        total: subtotal,
        observaciones: observaciones.trim(),
        vendedor: usuarioLog || '',
        items: items.map((it) => ({
          codigo: it.codigo,
          descripcion: it.descripcion,
          cantidad: it.cantidad,
          precio: it.precio,
          descuento: it.descuento || 0,
          impuesto: it.impuesto || 0,
          subtotal: it.subtotal,
          tipo: it.tipo || 'producto',
          es_inventario: true,
          producto_codigo: it.producto_codigo || it.codigo,
        })),
      };

      const endpoint = esEdicion
        ? `${servidor}/api/ordenesdepedidos_actualizar`
        : `${servidor}/api/ordenesdepedidos_guardar`;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          orden: payloadOrden,
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        showError(err.Error || 'No se pudo guardar la orden');
        return;
      }
      const data = await response.json();
      showSuccess(
        esEdicion
          ? `Orden de pedido #${data.numero} actualizada`
          : `Orden de pedido #${data.numero} guardada (vendedor: ${usuarioLog})`
      );
      limpiarFormulario();
      if (typeof onGuardado === 'function') onGuardado(data);
      if (typeof onClose === 'function') onClose();
    } catch {
      showError('Error de conexión al guardar la orden');
    } finally {
      setGuardando(false);
    }
  };

  if (!open) return null;

  const contenido = !permisoLeer ? (
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
      <Card elevation={0} sx={{ ...cardShellSx, maxWidth: 560, width: '100%', textAlign: 'center', p: 4 }}>
        <LockOutlinedIcon sx={{ fontSize: 48, color: FP.color.danger, mb: 2 }} />
        <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: FP.color.textPrimary, mb: 1 }}>
          Acceso denegado
        </Typography>
        <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem' }}>
          No tiene permiso del programa «{PROGRAMA_ORDEN_PEDIDO}» para crear órdenes de pedido.
        </Typography>
      </Card>
    </Box>
  ) : (
    <Box sx={{ bgcolor: FP.color.surface, minHeight: '100%' }}>
      <PageHeader
        title={esEdicion ? `Modificar orden #${ordenId}` : 'Nueva orden de pedido'}
        subtitle={
          nombreNegocio
            ? `${nombreNegocio} · Vendedor: ${usuarioLog || '—'}`
            : `Vendedor: ${usuarioLog || '—'}`
        }
        cta={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              startIcon={<InfoOutlinedIcon />}
              onClick={() => setDialogoConsultaProducto(true)}
              size="small"
              sx={{
                borderColor: FP.color.border,
                color: FP.color.textPrimary,
                bgcolor: '#FFFFFF',
                borderRadius: 1.4,
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
              }}
            >
              Consultar producto
            </Button>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={limpiarFormulario}
              disabled={!permisoModificar || guardando}
              size="small"
              sx={{
                borderColor: FP.color.border,
                color: FP.color.textPrimary,
                bgcolor: '#FFFFFF',
                borderRadius: 1.4,
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
              }}
            >
              Limpiar
            </Button>
            <Button
              variant="contained"
              startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={guardarOrden}
              disabled={!permisoModificar || guardando || cargando}
              size="small"
              sx={{
                bgcolor: FP.color.navy,
                color: '#FFF',
                borderRadius: 1.4,
                textTransform: 'none',
                fontWeight: 700,
                boxShadow: 'none',
                '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
              }}
            >
              {esEdicion ? 'Guardar cambios' : 'Guardar orden'}
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        {!permisoModificar && (
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              borderRadius: 2,
              border: `1px solid ${FP.color.border}`,
              bgcolor: FP.color.warningSoft,
            }}
          >
            Solo lectura: tiene permiso de consulta pero no puede crear ni modificar órdenes.
          </Alert>
        )}

        {cargando && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {/* Datos del documento */}
        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PersonIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Datos del documento
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Chip
              size="small"
              icon={<ReceiptLongIcon sx={{ fontSize: '16px !important' }} />}
              label={`Almacén principal: ${localidadAlmacenPrincipal || '—'}`}
              sx={{ fontWeight: 700, bgcolor: FP.color.surfaceAlt }}
            />
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  fullWidth
                  options={clientesFiltrados}
                  getOptionLabel={(opt) =>
                    `${opt.id || ''} - ${opt.descrip || opt.descripcion || ''}`
                  }
                  value={clienteSeleccionado}
                  onChange={(_, val) => setClienteSeleccionado(val)}
                  inputValue={busquedaCliente}
                  onInputChange={(_, val) => setBusquedaCliente(val)}
                  disabled={!permisoModificar}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cliente"
                      required
                      placeholder="Buscar cliente…"
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  label="Tipo de venta"
                  value={tipoFactura}
                  onChange={(e) => setTipoFactura(e.target.value)}
                  fullWidth
                  disabled={!permisoModificar}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                >
                  <MenuItem value="credito">Crédito</MenuItem>
                  <MenuItem value="contado">Contado</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Días de crédito"
                  type="number"
                  value={diasCredito}
                  onChange={(e) => setDiasCredito(e.target.value)}
                  disabled={!permisoModificar || tipoFactura !== 'credito'}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Forma de pago"
                  value={formaPago}
                  onChange={(e) => setFormaPago(e.target.value)}
                  disabled={!permisoModificar}
                  fullWidth
                  placeholder="Efectivo, Transferencia…"
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Validez (días)"
                  type="number"
                  value={validezDias}
                  onChange={(e) => setValidezDias(e.target.value)}
                  disabled={!permisoModificar}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Vendedor"
                  value={usuarioLog || ''}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                  helperText="Usuario que realiza la orden"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Tipo de precio"
                  value={tipoPrecio}
                  onChange={(e) => setTipoPrecio(e.target.value)}
                  helperText={
                    listaPrecioCliente
                      ? `Lista del cliente: ${listaPrecioCliente.codigo} - ${listaPrecioCliente.descripcion || ''}`
                      : 'Mayor usa el campo Mayor de cada producto'
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                >
                  <MenuItem value="detalle">Detalle</MenuItem>
                  <MenuItem value="mayor">Mayor (precio x mayor del producto)</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Observaciones"
                  value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)}
                  disabled={!permisoModificar}
                  fullWidth
                  multiline
                  minRows={2}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Ítems */}
        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ShoppingCartIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Ítems de la orden
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogoProductoAbierto(true)}
              disabled={!permisoModificar}
              size="small"
              sx={{
                bgcolor: FP.color.navy,
                color: '#FFF',
                borderRadius: 1.4,
                textTransform: 'none',
                fontWeight: 700,
                boxShadow: 'none',
                '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
              }}
            >
              Agregar producto
            </Button>
          </Box>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              border: `1px solid ${FP.color.border}`,
              borderRadius: 2,
              overflow: 'auto',
              m: { xs: 1.5, md: 2 },
              maxHeight: 420,
            }}
          >
            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', minWidth: 720 }}>
              <colgroup>
                {['12%', '34%', '12%', '14%', '14%', '14%'].map((w, i) => (
                  <col key={i} style={{ width: w }} />
                ))}
              </colgroup>
              <TableBody>
                <TableRow
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    '& td': {
                      border: 'none',
                      borderBottom: `2px solid ${FP.color.border}`,
                      bgcolor: FP.color.surfaceAlt,
                    },
                  }}
                >
                  <TableCell>
                    <Typography component="span" sx={cellHeadSx}>
                      Código
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={cellHeadSx}>
                      Descripción
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                      Cantidad
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                      Precio
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                      Subtotal
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                      Acciones
                    </Typography>
                  </TableCell>
                </TableRow>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5, color: FP.color.textMuted }}>
                      Agregue productos a la orden
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((it) => (
                    <TableRow key={it.id} hover>
                      <TableCell sx={{ fontWeight: 700, color: FP.color.textPrimary }}>{it.codigo}</TableCell>
                      <TableCell
                        sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={it.descripcion || ''}
                      >
                        {it.descripcion}
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          type="number"
                          size="small"
                          value={it.cantidad}
                          disabled={!permisoModificar}
                          onChange={(e) => actualizarCantidad(it.id, e.target.value)}
                          sx={{ width: 88, ...fpInputSx }}
                          inputProps={{ min: 0.01, step: 0.01 }}
                        />
                      </TableCell>
                      <TableCell align="right">${fmtMoney(it.precio)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>
                        ${fmtMoney(it.subtotal)}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => quitarItem(it.id)}
                          disabled={!permisoModificar}
                          sx={{ color: FP.color.danger }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Totales */}
        <Card elevation={0} sx={{ ...cardShellSx, mb: 2, maxWidth: 420, ml: 'auto' }}>
          <Box sx={filterHeaderSx}>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Totales
            </Typography>
          </Box>
          <CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ color: FP.color.textSecondary, fontWeight: 600 }}>Ítems</Typography>
              <Typography sx={{ fontWeight: 700 }}>{items.length}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ color: FP.color.navy, fontWeight: 800, fontSize: '1.05rem' }}>
                Total
              </Typography>
              <Typography sx={{ color: FP.color.navy, fontWeight: 800, fontSize: '1.05rem' }}>
                ${fmtMoney(subtotal)}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );

  return (
    <>
      <Dialog open={open} onClose={() => !guardando && onClose?.()} fullScreen>
        <BarraVolver onExit={() => !guardando && onClose?.()} />
        {contenido}
      </Dialog>

      <Dialog
        open={dialogoProductoAbierto}
        onClose={() => setDialogoProductoAbierto(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary }}>Agregar producto</Typography>
          <IconButton onClick={() => setDialogoProductoAbierto(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Buscar"
              value={busquedaProducto}
              onChange={(e) => setBusquedaProducto(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: FP.color.textMuted, fontSize: 20 }} />,
              }}
              sx={fpInputSx}
            />
          </Stack>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              maxHeight: 400,
              border: `1px solid ${FP.color.border}`,
              borderRadius: 2,
              overflow: 'auto',
            }}
          >
            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', minWidth: 720 }}>
              <colgroup>
                {['18%', '36%', '14%', '14%', '18%'].map((w, i) => (
                  <col key={i} style={{ width: w }} />
                ))}
              </colgroup>
              <TableBody>
                <TableRow
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    '& td': {
                      border: 'none',
                      borderBottom: `2px solid ${FP.color.border}`,
                      bgcolor: FP.color.surfaceAlt,
                    },
                  }}
                >
                  <TableCell>
                    <Typography component="span" sx={cellHeadSx}>
                      Código
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={cellHeadSx}>
                      Descripción
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                      Precio
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                      Cant.
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                      Acciones
                    </Typography>
                  </TableCell>
                </TableRow>
                {productosFiltrados.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4, color: FP.color.textMuted }}>
                      No se encontraron productos
                    </TableCell>
                  </TableRow>
                ) : (
                  productosFiltrados.map((p) => (
                    <TableRow key={p.codigo} hover>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          color: FP.color.textPrimary,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: 0,
                        }}
                        title={String(p.codigo || '')}
                      >
                        {p.codigo}
                      </TableCell>
                      <TableCell
                        sx={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: 0,
                        }}
                        title={p.descripcion || ''}
                      >
                        {p.descripcion}
                      </TableCell>
                      <TableCell align="right">${fmtMoney(p.precio)}</TableCell>
                      <TableCell align="center">
                        <TextField
                          type="number"
                          size="small"
                          value={getCantidadProducto(p.codigo)}
                          onChange={(e) => setCantidadProducto(p.codigo, e.target.value)}
                          sx={{ width: 72, ...fpInputSx }}
                          inputProps={{ min: 0.01, step: 0.01 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => agregarProducto(p)}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            bgcolor: FP.color.navy,
                            boxShadow: 'none',
                            '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                          }}
                        >
                          Agregar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#FAFBFE', borderTop: `1px solid ${FP.color.border}`, px: 3, py: 2 }}>
          <Button
            onClick={() => setDialogoProductoAbierto(false)}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 1.4,
              borderColor: FP.color.border,
              color: FP.color.textPrimary,
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <ConsultaProducto
        open={dialogoConsultaProducto}
        onClose={() => setDialogoConsultaProducto(false)}
      />
    </>
  );
};

export default NuevaOrdenPedido;
